// src/modules/settings/SettingsForm.js
import React, { useState, useEffect } from 'react';
import { createSetting, getSetting, updateSetting } from './settingsService';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Input from '../../components/shared/Input';
import TextArea from '../../components/shared/TextArea';
import Button from '../../components/shared/Button';
import DashboardLayout from '../../layouts/DashboardLayout';

const FormContainer = styled.div`
  padding: 20px;
`;

const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

const AddFieldButton = styled.button`
  background-color: ${(props) => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 5px;
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.colors.primaryDark};
  }
`;

const RemoveFieldButton = styled.button`
  background-color: red;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 8px 12px;
  margin-left: 10px;
  cursor: pointer;
`;

const SettingsForm = () => {
  const { id } = useParams();  // Grab the setting ID if editing
  const navigate = useNavigate();  // useNavigate replaces useHistory
  const [setting, setSetting] = useState({ key: '', value: '' });
  const [fields, setFields] = useState([]);  // Dynamic key-value fields

  useEffect(() => {
    if (id) {
      const fetchSetting = async () => {
        const data = await getSetting(id);
        setSetting({ key: data.key, value: JSON.stringify(data.value) });
        setFields(Object.entries(data.value));  // Convert JSON object into array of key-value pairs
      };

      fetchSetting();
    }
  }, [id]);

  // Handle input change for each dynamic field
  const handleFieldChange = (index, key, value) => {
    const updatedFields = [...fields];
    updatedFields[index] = [key, value];  // Update the specific field
    setFields(updatedFields);
  };

  // Add a new field
  const addField = () => {
    setFields([...fields, ['', '']]);  // Add empty key-value pair
  };

  // Remove a field
  const removeField = (index) => {
    const updatedFields = fields.filter((_, i) => i !== index);  // Remove the field at the specific index
    setFields(updatedFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Convert the fields back into a key-value object
    const valueObject = Object.fromEntries(fields);

    const newSetting = {
      key: setting.key,
      value: valueObject,
    };

    if (id) {
      await updateSetting(id, newSetting);  // Update if editing
    } else {
      await createSetting(newSetting);  // Create new if adding
    }

    navigate('/settings');
  };

  return (
    <DashboardLayout>
    <FormContainer>
      <form onSubmit={handleSubmit}>
        <Input
          type="text"
          placeholder="Setting Key"
          value={setting.key}
          onChange={(e) => setSetting({ ...setting, key: e.target.value })}
        />
        {/* Dynamic Fields for key-value pairs */}
        {fields.map(([key, value], index) => (
          <FieldGroup key={index}>
            <Input
              type="text"
              placeholder="Field Key"
              value={key}
              onChange={(e) => handleFieldChange(index, e.target.value, value)}
            />
            <TextArea
              placeholder="Field Value"
              value={value}
              onChange={(e) => handleFieldChange(index, key, e.target.value)}
              rows={3}
            />
            <RemoveFieldButton type="button" onClick={() => removeField(index)}>
              Remove
            </RemoveFieldButton>
          </FieldGroup>
        ))}
        <AddFieldButton type="button" onClick={addField}>
          Add Field
        </AddFieldButton>
        <Button type="submit">Save</Button>
      </form>
    </FormContainer>
    </DashboardLayout>
  );
};

export default SettingsForm;
